import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";
import { PaginationControls } from "@/components/common/PaginationControl";

const PLACEHOLDER_IMAGE = "/placeholder.svg";

interface ProductAssignmentPanelProps {
  selectedCategoryId: string;
  setSelectedCategoryId: (id: string) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  brandFilter: string;
  setBrandFilter: (filter: string) => void;
  merchantFilter: string;
  setMerchantFilter: (filter: string) => void;
  categoryProducts: any[];
  categoryProductsLoading: boolean;
  totalProducts: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  brands: any[];
  brandsLoading: boolean;
  merchants: any[];
  merchantsLoading: boolean;
  categories: any[];
  onAssignProduct: () => void;
  onRemoveProduct: (productId: number) => void;
  onMoveProduct: (productId: number, newCategoryId: string) => void;
  onResetFilters: () => void;
  flattenCategories: (categories: any[]) => any[];
}

export const ProductAssignmentPanel: React.FC<ProductAssignmentPanelProps> = ({
  selectedCategoryId,
  searchText,
  setSearchText,
  brandFilter,
  setBrandFilter,
  merchantFilter,
  setMerchantFilter,
  categoryProducts,
  categoryProductsLoading,
  totalProducts,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  brands,
  brandsLoading,
  merchants,
  merchantsLoading,
  categories,
  onAssignProduct,
  onRemoveProduct,
  onMoveProduct,
  onResetFilters,
  flattenCategories,
}) => {
  return (
    <div className="flex flex-col flex-1 h-full">
      <Card className="flex-1 h-full min-h-[700px]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-bold">Assign Products</CardTitle>
          <Button
            onClick={onAssignProduct}
            disabled={!selectedCategoryId}
            title={
              !selectedCategoryId
                ? "Select a category to assign products"
                : undefined
            }
          >
            Assign Product
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col h-full">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Input
              placeholder="Search by name"
              className="flex-1"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select value={brandFilter} onValueChange={setBrandFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Select Brand" />
              </SelectTrigger>
              <SelectContent>
                {brandsLoading ? (
                  <div className="px-3 py-2 text-gray-500">Loading...</div>
                ) : brands.length === 0 ? (
                  <div className="px-3 py-2 text-gray-500">No brands</div>
                ) : (
                  brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id.toString()}>
                      {brand.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Select value={merchantFilter} onValueChange={setMerchantFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select Merchant" />
              </SelectTrigger>
              <SelectContent>
                {merchantsLoading ? (
                  <div className="px-3 py-2 text-gray-500">Loading...</div>
                ) : merchants.length === 0 ? (
                  <div className="px-3 py-2 text-gray-500">No merchants</div>
                ) : (
                  merchants.map((merchant) => (
                    <SelectItem
                      key={merchant.id}
                      value={merchant.id.toString()}
                    >
                      {merchant.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Button variant="ghost" type="button" onClick={onResetFilters}>
              Reset
            </Button>
          </div>
          <div className="mb-2 font-semibold text-gray-700">
            Category:{" "}
            <Badge>
              {flattenCategories(categories).find(
                (c) => c.id === selectedCategoryId
              )?.name || "-"}
            </Badge>
          </div>
          <div className="flex-1 min-h-0 flex flex-col mb-5">
            <DataTable
              title=""
              data={categoryProductsLoading ? [] : categoryProducts}
              columns={[
                {
                  header: "Product Image",
                  cell: (row) => (
                    <img
                      src={row.image || PLACEHOLDER_IMAGE}
                      alt={row.name}
                      className="w-12 h-12 object-contain rounded border bg-gray-50"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = PLACEHOLDER_IMAGE;
                      }}
                    />
                  ),
                },
                { header: "Product Name", cell: (row) => row.name },
                { header: "Brand", cell: (row) => row.brand },
                { header: "Merchant", cell: (row) => row.merchant },
                { header: "Shipped From", cell: (row) => row.shippedFrom },
                {
                  header: "Action",
                  cell: (row) => (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost" className="p-1">
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => onRemoveProduct(row.id)}
                        >
                          Remove from category
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <div className="w-full">
                            <Select
                              onValueChange={(catId) =>
                                onMoveProduct(row.id, catId)
                              }
                            >
                              <SelectTrigger className="w-full text-left px-0 border-none shadow-none focus:ring-0 focus:border-none">
                                <SelectValue placeholder="Move to..." />
                              </SelectTrigger>
                              <SelectContent>
                                {flattenCategories(categories)
                                  .filter((c) => c.id !== selectedCategoryId)
                                  .map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                      {c.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ),
                },
              ]}
            />
          </div>
          {categoryProducts && categoryProducts.length > 0 && (
            <div className="flex items-center justify-between mt-4 flex-shrink-0 w-full self-end mt-auto mt-5">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Rows per page:</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setCurrentPage(0);
                  }}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue placeholder={pageSize.toString()} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <PaginationControls
                totalPages={Math.ceil(totalProducts / pageSize)}
                currentPage={currentPage}
                onNextHandler={() =>
                  setCurrentPage((p) =>
                    Math.min(Math.ceil(totalProducts / pageSize) - 1, p + 1)
                  )
                }
                onPrevHandler={() => setCurrentPage((p) => Math.max(0, p - 1))}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
